/**
 * Client for the snapshot editor endpoints.
 *
 * Unlike the rest of the dashboard's API calls these post JSON rather than
 * urlencoded bodies: a snapshot carries its assets inline as base64 and runs to
 * several megabytes, which percent-encoding would inflate for no reason. The
 * `/snapshotEditor` router parses its own bodies with a matching limit.
 */

import _ from "lodash";
import { apiURI } from "./apiURI";

/** DOM id of the editor's preview iframe — the postMessage bridge keys off it. */
export const SNAPSHOT_IFRAME_ID = "snapshotPreviewIframe";

/**
 * A base's snapshot-editor configuration, read the same way the server's
 * editorConfig.js reads it — the client and the server must never disagree
 * about which bases have this editor.
 *
 * Three shapes are accepted:
 *
 *   snapshotEditor: { flow, taskDelivery, exportAsVersion }   current
 *   ecommEditor: { … }                                        the field's old
 *                                                             name, read until
 *                                                             the seeds sync
 *   <field>: true                                             the original
 *                                                             boolean: the
 *                                                             applovinEcomm
 *                                                             flow, delivery on
 *
 * A configured editor without a flow is not usable — every pool the editor
 * touches is derived from the flow name — so it reads as "not an editor base"
 * rather than quietly falling back to another pipeline's snapshots.
 */
function readConfig(base) {
  const raw = _.get(base, "snapshotEditor", undefined) !== undefined
    ? _.get(base, "snapshotEditor")
    : _.get(base, "ecommEditor", false);

  if (raw === true) {
    return { enabled: true, flow: "applovinEcomm", taskDelivery: true, exportAsVersion: false };
  }
  if (!raw || typeof raw !== "object") {
    return { enabled: false, flow: null, taskDelivery: false, exportAsVersion: false };
  }

  const flow = _.get(raw, "flow") || null;
  const enabled = Boolean(flow);
  return {
    enabled,
    flow,
    taskDelivery: enabled && raw.taskDelivery === true,
    exportAsVersion: enabled && raw.exportAsVersion === true,
  };
}

/** True for a base the snapshot editor replaces the normal version editor on. */
export function isSnapshotEditorBase(base) {
  return readConfig(base).enabled;
}

/**
 * The agentContainer flow this base drives, or null.
 *
 * Shown to the user rather than chosen by them: the server reads the flow off
 * the base document and ignores anything the client sends, because which
 * pipeline a session runs — and therefore which repository its work is merged
 * into — is a property of the base being edited.
 */
export function snapshotFlowOf(base) {
  return readConfig(base).flow;
}

/**
 * Whether this base has the delivery half of the pipeline (AppLovin preview,
 * Drive, Asana). Bases that only produce and export snapshots do not, and the
 * server refuses those routes for them — so the UI must not offer them.
 *
 * Off by default in the object form: delivery publishes outside the company, so
 * forgetting the field costs a missing button rather than an unintended send.
 */
export function hasTaskDelivery(base) {
  return readConfig(base).taskDelivery;
}

export function hasAdsManagerClaims(base) {
  return String(base?._id) === "6a5e0911b7b9c93b24824240" && snapshotFlowOf(base) === "applovinEcomm";
}

export function adsManagerClaimSettings(baseID, settings) {
  return postJson("snapshotEditor/adsManagerClaimSettings", {
    baseID: String(baseID), ...(settings === undefined ? {} : { settings }),
  }, { timeoutMs: 15000 });
}

/** The job queue's state, or one of its actions (see routes/snapshotEditor.js); each answers with the new state. */
export function jobQueue(baseID, action, payload = {}) {
  return postJson(action ? `snapshotEditor/jobQueue/${action}` : "snapshotEditor/jobQueue", { baseID: String(baseID), ...payload }, { timeoutMs: 15000 });
}

/**
 * Whether snapshots from this base can be exported into another base as
 * playEngine versions.
 *
 * Off by default INCLUDING the legacy boolean form — unlike taskDelivery, which
 * that form always had. The export writes media files and a version document
 * into a different base, so it is granted per base rather than inherited.
 */
export function hasExportAsVersion(base) {
  return readConfig(base).exportAsVersion;
}

/**
 * The playEngine engine's dashboard-mode bundle, served straight from the
 * agentContainer's build output. Same engine build the agent verifies against,
 * so the preview matches what ships.
 */
export function getSnapshotPreviewUri() {
  return `${apiURI}/snapshotPreview/index.html`;
}

// The job plumbing — postJson, runJob, followJob and the JOB_* codes — lives in
// jobsApi.js so the versionedPlayEngine editor can poll its own status route.
// Re-exported here because this is where the editor has always imported them.
import {
  followJob,
  postJson,
  runJob,
  JOB_CONNECTION_LOST,
  JOB_LOST,
  JOB_NOT_STARTED,
} from "./jobsApi";

export { followJob, JOB_CONNECTION_LOST, JOB_LOST, JOB_NOT_STARTED };

/** The bare failure code out of a `{ ok:false }` response, whatever its shape. */
export function jobFailureCode(response) {
  const code = _.get(response, "error.error") || _.get(response, "error");
  return typeof code === "string" ? code : null;
}

/**
 * A failure code in the words the person who pressed the button needs.
 *
 * `repeatable` is the caller saying whether running the same thing again is
 * harmless. It is not decoration: a build overwrites its own artifacts, so
 * "try again" is the right advice; a delivery posts a SECOND Asana comment and
 * re-uploads to Drive (the container stamps the steps but does not skip the
 * ones already done), so the same words would be a trap.
 *
 * Anything not listed falls through to the code itself — the controllers speak
 * in codes on purpose, and a code nobody has written a sentence for is still
 * more useful than a shrug.
 */
/**
 * Where one stage of a running job stands, given every stage reported so far.
 *
 * A job announces a stage as it STARTS — `report({ type: "stage", stage })` on
 * the server — and never as it ends. So the last stage heard from is the one
 * running, and everything before it has finished. Reading it the other way
 * round ticks a step off while it is still going: a wrong that looks right, and
 * shows up only as someone closing the dialog too early.
 *
 * Callers pass the stage names they render; a stage they do not know about
 * simply moves the others along, so the two lists stay independent.
 *
 * @param {string[]} stagesSeen - stage names in arrival order
 * @returns {"done"|"active"|"pending"}
 */
export function stageState(stagesSeen, key) {
  const index = (stagesSeen || []).lastIndexOf(key);
  if (index === -1) return "pending";
  return index === stagesSeen.length - 1 ? "active" : "done";
}

export function describeJobFailure(response, { fallback = "The request failed.", repeatable = true } = {}) {
  const code = jobFailureCode(response);
  if (!code) return fallback;

  switch (code) {
  case JOB_CONNECTION_LOST:
    return repeatable
      ? "The connection to the server was lost. The job kept running there — wait a moment " +
          "and check the result before starting another one."
      : "The connection to the server was lost, but the work kept running there and has most " +
          "likely finished. Do not send it again before checking Drive and Asana — a second " +
          "send posts a second Asana comment.";
  case JOB_LOST:
    return repeatable
      ? "The server restarted while this was running, so its record is gone. The work itself " +
          "may well have finished — check before starting another one."
      : "The server restarted while this was running, so its record is gone. The steps may well " +
          "have completed anyway — check Drive and Asana before sending it again.";
  case JOB_NOT_STARTED:
    return "The request never reached the server, so nothing was started. Check your connection " +
        "and try again.";
  case "TASK_BUSY":
    return "Another build or delivery for this task is still running — wait for it to finish.";
  case "STAGING_MISSING":
    return "The built files are no longer on disk. Build the task again before sending it.";
  default:
    // The delivery controller wraps the container's own refusal in its message.
    if (code.startsWith("DELIVERY_REQUEST_FAILED") && code.includes("TASK_BUSY")) {
      return "Another build or delivery for this task is still running — wait for it to finish.";
    }
    return code;
  }
}

/**
 * The same sentence, plus the per-snapshot reasons underneath it.
 *
 * The controllers send the failures alongside the code — the whole point of
 * NO_ARTIFACTS_UPLOADED is that something under it explains itself — and a
 * message that shows only the code sends people to the container logs for a
 * sentence that was already in the response.
 */
export function describeJobFailureWithReasons(response, options = {}) {
  let message = describeJobFailure(response, options);

  const failures = _.get(response, "error.failed") || [];
  if (failures.length) {
    const lines = failures
      .slice(0, 3)
      .map((item) => `${item.snapshot || "?"}: ${item.error || "unknown"}`);
    if (failures.length > lines.length) lines.push(`…and ${failures.length - lines.length} more`);
    message = `${message} — ${lines.join(" | ")}`;
  }

  const warnings = _.get(response, "error.warnings") || [];
  if (warnings.length) message = `${message} · ${warnings[0]}`;

  return message;
}

/**
 * The long jobs this user still has running on this base.
 *
 * Asked once when the editor mounts: builds and deliveries outlive the dialog
 * that started them, so this is how a reloaded page — or one whose dialog was
 * closed an hour ago — finds them again and follows them with `followJob`.
 */
export function listSnapshotJobs(baseID) {
  return postJson("snapshotEditor/listJobs", { baseID: String(baseID) });
}

/** Snapshot summaries for the left-hand tree. */
export function listSnapshots(baseID) {
  return postJson("snapshotEditor/listSnapshots", { baseID: String(baseID) });
}

/** One snapshot in full, with the contentHash a later save must quote back. */
export function getSnapshot(baseID, fileName) {
  return postJson("snapshotEditor/getSnapshot", { baseID: String(baseID), fileName });
}

/**
 * Apply changes to a snapshot file.
 *
 * `contentHash` must be the one the client last read; a 409 means the file
 * changed underneath (the agent writes to the same folder) and carries the
 * current snapshot so the caller can recover without another round trip.
 */
export function saveSnapshot(baseID, fileName, contentHash, patches, removedPaths) {
  return postJson("snapshotEditor/saveSnapshot", {
    baseID: String(baseID),
    fileName,
    contentHash,
    patches,
    removedPaths,
  });
}

// ── Design descriptors ──────────────────────────────────────────────────────
// What TYPE each globalVariables path is, so the Design tab renders a colour
// picker rather than a text box. Stored beside the snapshots as its own file,
// shared by every snapshot a flow generates from the same template.

/**
 * The design this snapshot resolves to.
 *
 * `design: null` is a normal answer, not a failure — most snapshots have no
 * descriptor yet, and the tab offers to generate one.
 */
export function getSnapshotDesign(baseID, fileName) {
  return postJson("snapshotEditor/getSnapshotDesign", { baseID: String(baseID), fileName });
}

/**
 * Write a whole design.
 *
 * `contentHash` is the one the last read handed back; a 409 means the file
 * changed underneath and carries the current design. Pass `"*"` to overwrite
 * deliberately, and omit it entirely when creating.
 */
export function saveSnapshotDesign(baseID, { designId, design, contentHash }) {
  return postJson("snapshotEditor/saveSnapshotDesign", {
    baseID: String(baseID),
    designId,
    design,
    ...(contentHash ? { contentHash } : {}),
  });
}

/**
 * Every design on the flow, for the picker.
 *
 * With `fileName`, each entry also carries how much of it that snapshot holds
 * (`matchingFields` / `matchableFields` / `matchRatio`) — the picker hides the
 * ones written for another game.
 */
export function listSnapshotDesigns(baseID, fileName) {
  return postJson("snapshotEditor/listSnapshotDesigns", {
    baseID: String(baseID),
    ...(fileName ? { fileName: String(fileName) } : {}),
  });
}

// ── Toolbox scripts ─────────────────────────────────────────────────────────
// The Toolbox tab's buttons: a JS file per template in the flow's
// snapshots/toolboxScripts folder, bound like a design. Served as text and run
// in a Web Worker — see modules/snapshotToolbox/runner.js.

/** The toolbox this snapshot resolves to; `source: null` when it has none. */
export function getSnapshotToolbox(baseID, fileName) {
  return postJson("snapshotEditor/getSnapshotToolbox", { baseID: String(baseID), fileName });
}

/** Every toolbox on the flow, for the picker. */
export function listSnapshotToolboxes(baseID) {
  return postJson("snapshotEditor/listSnapshotToolboxes", { baseID: String(baseID) });
}

/**
 * File-level operations on the library.
 *
 * @param {string} operation  setFolder | rename | duplicate | discard | renameFolder
 * @param {object} payload    the fields that operation needs
 */
export function mutateSnapshot(baseID, operation, payload) {
  return postJson("snapshotEditor/mutateSnapshot", {
    baseID: String(baseID),
    operation,
    ...payload,
  });
}

/**
 * Build snapshots into delivered playables and get back a download link.
 *
 * The build is a matrix — snapshots × languages × networks — and each cell is a
 * webpack run, so this runs as a background job; pass `onProgress` to show the
 * cells landing rather than a spinner with no end.
 */
export function exportSnapshots(baseID, fileNames, folderName, options = {}) {
  return runJob(
    "snapshotEditor/exportSnapshots",
    {
      baseID: String(baseID),
      fileNames,
      folderName,
      languages: options.languages,
      networks: options.networks,
      loadingScreen: options.loadingScreen,
      subsetFonts: options.subsetFonts,
      ...(options.jobMeta ? { jobMeta: options.jobMeta } : {}),
    },
    { onProgress: options.onProgress, onConnectionState: options.onConnectionState }
  );
}

/**
 * A shareable link for ONE snapshot.
 *
 * Lands on demo.playin.com.tr like the version editors' external preview, but
 * under this editor's own `snapshot_` token: no mongo record is written and none
 * of the company's plan tests is spent. Answers with both links — `url` opens
 * the site's viewer, `rawUrl` is the playable itself. Same snapshot and network
 * → same pair, so a link already sent keeps showing the current build.
 *
 * One webpack run, so it is a background job like the export.
 */
export function previewSnapshot(baseID, fileName, options = {}) {
  return runJob(
    "snapshotEditor/previewSnapshot",
    {
      baseID: String(baseID),
      fileName,
      ...(options.network ? { network: options.network } : {}),
      ...(options.language ? { language: options.language } : {}),
      ...(options.loadingScreen ? { loadingScreen: options.loadingScreen } : {}),
      ...(options.subsetFonts === false ? { subsetFonts: false } : {}),
    },
    { onProgress: options.onProgress, onConnectionState: options.onConnectionState }
  );
}

/**
 * Preview one delivery task — a folder, which is one Asana task.
 *
 * Builds every SIP in the folder and mirrors them onto the task's own S3
 * folder. The returned links open in a browser instead of downloading, and a
 * later preview of the same folder overwrites the same objects, so a link that
 * has already been shared keeps showing the current build.
 *
 * Long-running — one webpack run per SIP — so it runs as a background job and
 * reports each SIP through `onProgress` as it lands.
 */
export function previewTask(baseID, folderPath, options = {}) {
  return runJob(
    "snapshotEditor/previewTask",
    {
      baseID: String(baseID),
      folderPath,
      // Name overrides belong here as much as on delivery: the build applies
      // them, so the preview shows each SIP under the name it will ship as.
      ...(options.naming ? { naming: options.naming } : {}),
      ...(options.loadingScreen ? { loadingScreen: options.loadingScreen } : {}),
      ...(options.subsetFonts === false ? { subsetFonts: false } : {}),
      ...(options.jobMeta ? { jobMeta: options.jobMeta } : {}),
    },
    { onProgress: options.onProgress, onConnectionState: options.onConnectionState }
  );
}

/**
 * Ship a previewed task: the AppLovin preview tool, Google Drive, then the
 * Asana comment. Sends exactly what the preview showed — nothing is rebuilt.
 *
 * Every call runs all three steps. A task does not stay delivered — feedback
 * comes back and it goes out again — so the links have to point at the build
 * that was just made, which only re-running produces.
 */
export function deliverTask(baseID, folderPath, options = {}) {
  return runJob(
    "snapshotEditor/deliverTask",
    {
      baseID: String(baseID),
      folderPath,
      // Per-token overrides of the delivered names. Only the tokens present are
      // replaced — everything else keeps what the snapshot's own name carried.
      ...(options.naming ? { naming: options.naming } : {}),
      ...(options.steps ? { steps: options.steps } : {}),
      ...(options.dryRun ? { dryRun: true } : {}),
      ...(options.creator ? { creator: options.creator } : {}),
      // "adsmanager" delivers by ACS Ads Manager's rules; absent is Audience+.
      ...(options.workflow ? { workflow: options.workflow } : {}),
      ...(options.jobMeta ? { jobMeta: options.jobMeta } : {}),
    },
    { onProgress: options.onProgress, onConnectionState: options.onConnectionState }
  );
}

/**
 * Whether a folder's Asana ticket is an ACS Ads Manager one — which decides the
 * dialog — and, when it is, what its delivery needs: the Drive folder, the open
 * subtask, the ACS Owner and the file names the description fixes.
 */
export function asanaAdsManagerContext(baseID, folderPath) {
  return postJson("snapshotEditor/asanaAdsManagerContext", {
    baseID: String(baseID),
    folderPath,
  });
}

/**
 * Suggested values for the extra tags of the listed names: `items` is
 * `[{ id, template, fileName, exportName }]`. The answer is
 * `{ filled: {[id]: {tag: value}}, unresolved: {[id]: [tag]}, error }` —
 * suggestions only; a person confirms them before anything is built.
 */
export function asanaAdsManagerFillTags(baseID, folderPath, items) {
  return postJson("snapshotEditor/asanaAdsManagerFillTags", {
    baseID: String(baseID),
    folderPath,
    items,
  }, { timeoutMs: 60 * 1000 });
}

export function asanaCreatorContext(baseID, folderPath) {
  return postJson("snapshotEditor/asanaCreatorContext", {
    baseID: String(baseID),
    folderPath,
  });
}

/**
 * The open Asana tasks a folder can be bound to, the folder's own name first.
 *
 * Slow enough to be worth a spinner: the container pages through every
 * workspace of the shared account.
 */
export function listAsanaTasks(baseID, folderPath) {
  return postJson("snapshotEditor/listAsanaTasks", {
    baseID: String(baseID),
    ...(folderPath ? { folderPath } : {}),
  });
}

/**
 * Bind a folder to its Asana task. With no `query` the folder's own name is
 * used, which is the convention. A name matching more than one open task comes
 * back as 409 with candidates rather than a guess.
 */
export function bindAsanaTask(baseID, folderPath, query) {
  return postJson("snapshotEditor/bindAsanaTask", {
    baseID: String(baseID),
    folderPath,
    ...(query ? { query } : {}),
  });
}

/**
 * The playEngine bases a snapshot may be exported into.
 *
 * `targetCompanyID` points the list at another company's grant — the version is
 * going to live there, so it is that company's bases the picker must offer, not
 * the ones the exporting user happens to see.
 */
export function listVersionTargets(baseID, { targetCompanyID } = {}) {
  return postJson("snapshotEditor/listVersionTargets", {
    baseID: String(baseID),
    ...(targetCompanyID ? { targetCompanyID: String(targetCompanyID) } : {}),
  });
}

/**
 * The playEngine bases a version can be pulled OUT of, in one company. Same list
 * as the export targets, asked for the opposite reason — a separate endpoint
 * rather than the same one, because the two directions are granted separately.
 *
 * `sourceCompanyID` is optional and defaults, server-side, to the company being
 * looked at. Naming another one only works for a company the user may move
 * playables between.
 */
export function listVersionSources(baseID, { sourceCompanyID } = {}) {
  return postJson("snapshotEditor/listVersionSources", {
    baseID: String(baseID),
    ...(sourceCompanyID ? { sourceCompanyID: String(sourceCompanyID) } : {}),
  });
}

/**
 * One base's versions in one company, as names only — the import dialog's
 * second dropdown. Not versionPlayEngine/find: that one is fixed to the active
 * company and does not check base access.
 */
export function listSourceVersions(baseID, { sourceBaseID, sourceCompanyID } = {}) {
  return postJson("snapshotEditor/listSourceVersions", {
    baseID: String(baseID),
    sourceBaseID: String(sourceBaseID),
    ...(sourceCompanyID ? { sourceCompanyID: String(sourceCompanyID) } : {}),
  });
}

/**
 * Pull an existing playEngine version back into the pool as a snapshot.
 *
 * The version's `@server:` asset references are inlined server-side, so what
 * lands in the pool is an ordinary snapshot the editor can open.
 */
export function importVersionAsSnapshot(baseID, { sourceVersionID, fileName, folderPath }) {
  return postJson("snapshotEditor/importVersionAsSnapshot", {
    baseID: String(baseID),
    sourceVersionID: String(sourceVersionID),
    ...(fileName ? { fileName } : {}),
    ...(folderPath ? { folderPath } : {}),
  });
}

/**
 * Save a snapshot into another base as a playEngine version.
 *
 * `targetBaseID` is authorized server-side on its own — the picker is a
 * convenience, not the gate.
 */
export function exportSnapshotAsVersion(baseID, { targetBaseID, targetCompanyID, fileName, title }) {
  return postJson("snapshotEditor/exportSnapshotAsVersion", {
    baseID: String(baseID),
    targetBaseID: String(targetBaseID),
    ...(targetCompanyID ? { targetCompanyID: String(targetCompanyID) } : {}),
    fileName,
    title,
  });
}

/**
 * Optimize an image's bytes before they are embedded in a snapshot.
 *
 * Returns the original data URL untouched when the format is not optimizable
 * or the result would be bigger, so the caller can always use what comes back.
 */
export function optimizeImageSource(baseID, { source, fileName }) {
  return postJson("snapshotEditor/compressImage", {
    baseID: String(baseID),
    source,
    ...(fileName ? { fileName } : {}),
  });
}

/**
 * Also keep a locally-uploaded asset in the library's `customUploads/` folder.
 *
 * Opt-in: an upload always embeds its bytes in the snapshot, and this is the
 * extra step that makes the same file available to the next one. `source` is a
 * data URL for simple types; bitmap fonts pass `image` + `xml` instead.
 */
export function saveCustomAsset(baseID, { assetType, fileName, source, image, xml, json }) {
  return postJson("snapshotEditor/saveCustomAsset", {
    baseID: String(baseID),
    assetType,
    fileName,
    ...(source ? { source } : {}),
    ...(image ? { image } : {}),
    ...(xml ? { xml } : {}),
    ...(json ? { json } : {}),
  });
}

/**
 * Announce that the library on disk is no longer what a reader last saw.
 *
 * Panels hold listings of their own — the Toolbox tab keeps a list of the level
 * folders it found — and a folder discarded from the browser dialog would stay
 * on them until something happened to reload it, which is how a row comes to
 * point at a folder that is not there. Fired by the three calls that write, so
 * no caller has to remember to.
 */
const LIBRARY_CHANGED_EVENT = "playengine:library-changed";

function announceLibraryChange(response) {
  if (response && response.ok && typeof window !== "undefined") {
    window.dispatchEvent(new Event(LIBRARY_CHANGED_EVENT));
  }
  return response;
}

export { LIBRARY_CHANGED_EVENT };

/** One directory of the scraped asset library. */
export function listAssets(baseID, dirPath) {
  return postJson("snapshotEditor/listAssets", { baseID: String(baseID), dirPath: dirPath || "" });
}

/**
 * The same library as a toolbox script sees it: `libraryTree` lists a folder as
 * `[{ name, path, type: "tree" | "blob", size }]`, `libraryFiles` returns file
 * contents as base64. Paths are relative to the library root, as listAssets'
 * `relativePath` is.
 */
export function libraryTree(baseID, path, { recursive } = {}) {
  return postJson("snapshotEditor/libraryTree", {
    baseID: String(baseID),
    path: path || "",
    ...(recursive ? { recursive: true } : {}),
  });
}

export function libraryFiles(baseID, paths) {
  return postJson("snapshotEditor/libraryFiles", { baseID: String(baseID), paths });
}

/**
 * Best-effort: the library folder(s) whose name is the longest match found
 * inside `name` (normalized — case, whitespace and punctuation stripped).
 * Used to guess a snapshot's own folder when nothing recorded which one that
 * is — the caller should only act on this when it comes back with exactly
 * one match; more than one means the guess is genuinely ambiguous.
 *
 * @returns `{ available, matches: { name, relativePath }[] }`
 */
export function findAssetFolder(baseID, name) {
  return postJson("snapshotEditor/findAssetFolder", { baseID: String(baseID), name });
}

/**
 * A snapshot's own folder in the library, best guess — shared by every
 * "Library" entry point (the Assets tab's Add/Replace, Version Settings'
 * per-dependency picker) so they all land in the same place for the same
 * snapshot.
 *
 * `metadata.assetsFolder` is the reliable source — the pipeline stamps it
 * when it knows which folder it drew from (not every game type/flow does).
 * Failing that, this tries the snapshot's own names against the library's
 * folder names: an UNAMBIGUOUS hit (see `findAssetFolder`) is used, anything
 * less (no hit, or a genuine tie) is not — a wrong guess would land an
 * upload in a stranger's task folder without anyone noticing, worse than the
 * last-visited folder this falls back to.
 *
 * @param {string} baseID
 * @param {object} snapshot        editor.snapshot
 * @param {string} [loadedFileName] editor.loadedFileName — the last-resort candidate
 * @returns {Promise<string|undefined>}
 */
export async function guessSnapshotAssetsFolder(baseID, snapshot, loadedFileName) {
  const stamped = _.get(snapshot, "metadata.assetsFolder");
  if (typeof stamped === "string" && stamped) return stamped;

  const candidates = _.uniq(
    _.compact([
      _.get(snapshot, "metadata.exportFolder"),
      _.get(snapshot, "metadata.taskName"),
      _.get(snapshot, "metadata.name"),
      loadedFileName ? loadedFileName.replace(/\.json$/i, "") : null,
    ])
  );

  for (const name of candidates) {
    const result = await findAssetFolder(baseID, name);
    if (result.ok && _.get(result, "data.matches", []).length === 1) {
      return result.data.matches[0].relativePath;
    }
  }
  return undefined;
}

/**
 * Make a folder inside the library directory being browsed.
 *
 * The server sanitises the name and answers with what it actually created, so
 * the caller should show that rather than what was typed.
 */
export function createAssetFolder(baseID, { dirPath, name }) {
  return postJson("snapshotEditor/createAssetFolder", {
    baseID: String(baseID),
    dirPath: dirPath || "",
    name,
  }).then(announceLibraryChange);
}

/**
 * Put one file into the library directory being browsed. `source` is a data URL.
 *
 * A taken name is not overwritten — the server renames and reports the name it
 * used. Unlike `saveCustomAsset` this touches no snapshot: the file simply joins
 * the library where the browser is.
 */
export function uploadAssetToLibrary(baseID, { dirPath, fileName, source }) {
  return postJson("snapshotEditor/uploadAsset", {
    baseID: String(baseID),
    dirPath: dirPath || "",
    fileName,
    source,
  }).then(announceLibraryChange);
}

/**
 * What is behind these paths, without moving anything.
 *
 * Asked before the confirmation is drawn: the listing shows one directory, so
 * only the server can say that the folder about to go holds twelve files in
 * three subfolders — and that sentence is the confirmation's entire job.
 *
 * @returns entries as `{ relativePath, kind: "file"|"dir"|"missing", sizeBytes?,
 *   files?, folders? }` — counts are recursive, and `missing` means the listing
 *   this was read from has gone stale.
 */
export function inspectAssets(baseID, paths) {
  return postJson("snapshotEditor/mutateAsset", {
    baseID: String(baseID),
    operation: "inspect",
    paths,
  });
}

/**
 * Take an entry out of the library.
 *
 * Nothing is deleted: the server moves it into the library's `badones/` folder,
 * which nothing in the dashboard lists or can reach — the same bargain as
 * discarding a snapshot.
 *
 * @param {string[]} paths     the files to move. A bitmap font or an atlas is
 *   one tile and two files, so both halves travel in one call.
 * @param {"file"|"dir"} expect  what the caller believed it was acting on; the
 *   server refuses when the listing has gone stale under it.
 * @param {boolean} [recursive]  required for a folder that is not empty. Without
 *   it the server answers 409 FOLDER_NOT_EMPTY carrying `{ files, folders }`,
 *   which is what the confirmation is written from.
 */
export function discardAssets(baseID, { paths, expect, recursive }) {
  return postJson("snapshotEditor/mutateAsset", {
    baseID: String(baseID),
    operation: "discard",
    paths,
    expect,
    ...(recursive ? { recursive: true } : {}),
  }).then(announceLibraryChange);
}

/**
 * Absolute URL for a scraped asset. listAssets returns paths relative to the
 * API root, which the dev server is not served from.
 */
export function getAssetStaticUri(relativeUrl) {
  return `${apiURI}/${relativeUrl}`;
}

/**
 * Fetch a scraped asset over the static route and return it as a data URL,
 * ready to be embedded into the snapshot. Nothing is stored server-side — the
 * snapshot carries the bytes from here on.
 */
/**
 * Read a library entry's bytes into the shape the snapshot's asset map stores.
 *
 * Pair types (bitmap-font, atlas) carry two files and land as `{ image, xml }`;
 * everything else is a single data URL. Lives here rather than in a panel
 * because both the Assets tab and Version Settings pick from the same library
 * and must produce the same thing.
 */
export async function sourceFromLibraryEntry(entry) {
  if (!entry) return null;

  // Pair types come back as two URLs. The key the metadata half is stored under
  // is what the engine reads, so it differs by type: `xml` for a bitmap font,
  // `json` for an atlas.
  const metaKey = entry.assetType === "bitmap-font" ? "xml" : entry.assetType === "atlas" ? "json" : null;
  if (metaKey) {
    const [image, meta] = await Promise.all([
      fetchAssetAsDataUrl(entry.imageUrl),
      fetchAssetAsDataUrl(entry.metaUrl || entry[`${metaKey}Url`]),
    ]);
    return { image, [metaKey]: meta };
  }

  return fetchAssetAsDataUrl(entry.url);
}

export async function fetchAssetAsDataUrl(url) {
  const response = await fetch(getAssetStaticUri(url));
  if (!response.ok) throw new Error(`Could not read asset (${response.status})`);

  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
